import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchPostTrackingLabels } from '@/redux/socialListening/socialListeningActions';
import { identity, pickBy } from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { useApi } from '../../../../../hooks/useApi';
import {
  getAllUsageRequests,
  getContentCountStats,
  getEngagementBreakdownStats,
  getImpressionsAcrossPlatforms,
  getKeyPerformanceIndicators,
  getPostTrackingCampaignById,
  getPostTrackingCampaignStats,
  getPostTrackingPosts,
  getTopPerformingCreators,
  getTopPerformingPosts,
  getTotalViewsAcrossPlatforms,
} from '../../../../../services/postTracking.service';
import { dayjs } from '../../../../../utils/dayjs-utils';
import { SOCIAL_LISTENING_FILTERS } from '../../constants';

const CampaignDetailContext = React.createContext({
  activeTab: 'content',
  setActiveTab: () => {},
  loadingStats: false,
  campaignStats: null,
  selectedDays: -1,
  setSelectedDays: () => {},
  dateRange: [],
  setDateRange: () => {},
  selectedView: 'grid',
  setSelectedView: () => {},
  selectedContentType: null,
  setNewContentType: () => {},
  loadingContent: false,
  content: [],
  gridRef: null,
  isTrackingInProgress: true,
  showChartFtux: true,
  campaignDetails: {},
  loadingCampaignDetails: false,
  refetchCampaignDetails: () => {},
  onRemoveFilter: () => {},
  isNextContent: {
    isNext: false,
    nextCursor: null,
    page: null,
  },
  isLoadingMore: false,
  onLoadMoreClick: () => {},
  labelModalOpen: { post: null, openModal: null },
  setLabelModalOpen: (args) => {},

  // Advanced stats states
  loadingKPI: false,
  loadingTotalViews: false,
  loadingTopCreators: false,
  loadingTopPosts: false,
  loadingImpressions: false,
  loadingContentCount: false,
  loadingEngagement: false,

  // Advanced stats data
  keyPerformanceData: null,
  totalViewsData: null,
  topCreatorsData: [],
  topPostsData: [],
  impressionsData: null,
  contentCountData: null,
  engagementBreakdownData: null,
});

const PAGINATION_LIMIT = 15;

const getToalPages = (totalPostsCount) => {
  return Math.ceil(totalPostsCount / PAGINATION_LIMIT);
};

const CampaignDetailProvider = ({
  children,
  apiConfig = { getStats: true, getUsageRights: true, getCampaignDetails: true, postsApiMode: 'campaign' },
  defaultActiveTab = 'content',
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const gridRef = useRef();
  const abortControllerRef = useRef(null);

  const [activeTab, setActiveTab] = useState(() => location?.state?.openTab || defaultActiveTab);
  const [usageRequestsPage, setUsageRequestsPage] = useState(1);
  const [usageRequestsPages, setUsageRequestsPages] = useState(1);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [selectedDays, setSelectedDays] = useState(-1);
  const [dateRange, setDateRange] = useState([]);
  const [selectedView, setSelectedView] = useState('grid');
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [selectedPlatformType, setSelectedPlatformType] = useState('all');
  const [selectedLists, setSelectedLists] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [content, setContent] = useState({ posts: [], totalPosts: null });
  const [usageRightsContentType, setUsageRightsContentType] = useState('all');
  const [usageRightsStatus, setUsageRightsStatus] = useState('all');
  const [usageRequests, setUsageRequests] = useState({
    usageRights: [],
    totalUsageRights: 0,
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isNextContent, setIsNextContent] = useState({
    isNext: false,
    nextCursor: null,
    page: null,
    totalPages: null,
  });
  const [contentCurrentPage, setContentCurrentPage] = useState(1);
  const [labelModalOpen, setLabelModalOpen] = useState({ post: null, openModal: null });

  // Sort by impressions / likesCount / commentsCount SAR-365
  const [sortField, setSortField] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentModal, setCurrentModal] = useState(false);

  // User will never be able to see campaign details when it's tracking
  const isTrackingInProgress = false;

  // Advanced stats states
  const [loadingAdvancedStats, setLoadingAdvancedStats] = useState(false);
  const [keyPerformanceData, setKeyPerformanceData] = useState(null);
  const [totalViewsData, setTotalViewsData] = useState(null);
  const [topCreatorsData, setTopCreatorsData] = useState([]);
  const [topPostsData, setTopPostsData] = useState([]);
  const [impressionsData, setImpressionsData] = useState(null);
  const [contentCountData, setContentCountData] = useState(null);
  const [engagementBreakdownData, setEngagementBreakdownData] = useState(null);

  // Individual loading states
  const [loadingKPI, setLoadingKPI] = useState(false);
  const [loadingTotalViews, setLoadingTotalViews] = useState(false);
  const [loadingTopCreators, setLoadingTopCreators] = useState(false);
  const [loadingTopPosts, setLoadingTopPosts] = useState(false);
  const [loadingImpressions, setLoadingImpressions] = useState(false);
  const [loadingContentCount, setLoadingContentCount] = useState(false);
  const [loadingEngagement, setLoadingEngagement] = useState(false);

  const [showChartFtux, setShowChartFtux] = useState(true);

  const recalculateGrid = useCallback(() => {
    let counter = 0;
    const intervalId = setInterval(() => {
      gridRef?.current?.updateLayout();
      counter++;
      if (counter === 5) {
        clearInterval(intervalId);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    if (location?.state?.usageRightId) {
      setActiveTab('usage-rights');
    }
  }, [location]);

  const { isLoading: loadingCampaignDetails, triggerFetch: refetchCampaignDetails } = useApi(
    getPostTrackingCampaignById,
    {
      args: { campaignId: id },
      onSuccess: (res) => {
        setCampaignDetails(res.data);
        // Set to true if campaign is NOT older than 4 days
        const campaignCreatedDate = dayjs(res.data?.createdAt);
        const fourDaysAgo = dayjs().subtract(4, 'days');
        setShowChartFtux(!campaignCreatedDate.isBefore(fourDaysAgo));
      },
      enabled: apiConfig.getCampaignDetails,
    }
  );

  const { isLoading: loadingStats, triggerFetch: refetchStats } = useApi(getPostTrackingCampaignStats, {
    args: { campaignId: id },
    onSuccess: (res) => {
      if (res?.data?.campaigns?.[0]) setCampaignStats(res?.data?.campaigns?.[0]);
    },
    enabled: apiConfig.getStats,
  });

  const { isLoading: loadingRequests, triggerFetch: refetchUsageRequests } = useApi(getAllUsageRequests, {
    args: {
      campaignId: id,
      page: usageRequestsPage,
    },
    onSuccess: (res) => {
      if (res?.data) {
        setUsageRequests(res?.data);
        setUsageRequestsPages(res?.data?.totalPages);
      }
    },
    enabled: apiConfig.getUsageRights,
  });

  const refetchUsageRequestsWtihArgs = (args) => {
    const body = {
      campaignId: id,
      page: usageRequestsPage,
      contentType: usageRightsContentType,
      status: usageRightsStatus,
      ...args,
    };
    if (body.contentType === 'all') {
      body.contentType = undefined;
    }
    if (body.status === 'all') {
      body.status = undefined;
    }
    refetchUsageRequests({
      args: body,
    });
  };

  const setNewUsageRequestsContentType = (newContentType) => {
    setUsageRightsContentType(newContentType);
    setUsageRequestsPage(1);

    refetchUsageRequestsWtihArgs({
      page: 1,
      contentType: newContentType,
    });
  };

  const setNewUsageRequestsStatus = (newStatus) => {
    setUsageRightsStatus(newStatus);
    setUsageRequestsPage(1);

    refetchUsageRequestsWtihArgs({
      page: 1,
      status: newStatus,
    });
  };

  const defaultGetPostsArgs = useMemo(() => {
    if (apiConfig.postsApiMode === 'campaign') {
      return {
        campaignId: id,
        limit: PAGINATION_LIMIT,
        listIds: [],
        emailCampaignIds: [],
        sortField,
        sortOrder,
        page: 1,
      };
    } else {
      return {
        libraryIds: [id],
        limit: PAGINATION_LIMIT,
        listIds: [],
        emailCampaignIds: [],
        sortField,
        sortOrder,
      };
    }
  }, [id, apiConfig, sortField, sortOrder]);

  const { isLoading: loadingContent, triggerFetch: refetchPosts } = useApi(getPostTrackingPosts, {
    args: { ...defaultGetPostsArgs },
    enabled: false,
    onSuccess: (res) => {
      if (Array.isArray(res?.data?.posts)) {
        setContent((existingContent) => ({
          totalPosts: res?.data?.totalPostsCount,
          posts: [...existingContent.posts, ...res.data.posts],
        }));
        setIsNextContent({
          isNext: res?.data?.isNext,
          nextCursor: res?.data?.nextCursor,
          page: res?.data?.page,
          totalPages: getToalPages(res?.data?.totalPostsCount),
        });
      }
    },
  });

  useEffect(() => {
    setContentCurrentPage(1);
    refetchContent(getContentFilterBody({}, dateRange));
  }, [sortField, sortOrder]);

  useEffect(() => {
    dispatch(fetchPostTrackingLabels());
  }, []);

  const loadAdvancedStats = async (dateFilter = null) => {
    setLoadingAdvancedStats(true);
    // Set all individual loading states to true
    setLoadingKPI(true);
    setLoadingTotalViews(true);
    setLoadingTopCreators(true);
    setLoadingTopPosts(true);
    setLoadingImpressions(true);
    setLoadingContentCount(true);
    setLoadingEngagement(true);

    try {
      // Create base payload with campaignId
      const basePayload = { campaignId: id };

      // Add date filter for content count and engagement breakdown
      const dateFilterPayload = { ...basePayload };
      if (dateFilter && dateFilter.length === 2 && dateFilter[0] && dateFilter[1]) {
        dateFilterPayload.startDate = dayjs(dateFilter[0]).format('YYYY-MM-DD');
        dateFilterPayload.endDate = dayjs(dateFilter[1]).format('YYYY-MM-DD');
      }

      // Call all APIs in parallel using Promise.allSettled
      await Promise.allSettled([
        getKeyPerformanceIndicators(dateFilterPayload)
          .then((res) => {
            if (res?.data) setKeyPerformanceData(res.data);
          })
          .finally(() => setLoadingKPI(false)),

        getTotalViewsAcrossPlatforms(dateFilterPayload)
          .then((res) => {
            if (res?.data) setTotalViewsData(res.data);
          })
          .finally(() => setLoadingTotalViews(false)),

        getTopPerformingCreators(dateFilterPayload)
          .then((res) => {
            if (res?.data) setTopCreatorsData(res.data);
          })
          .finally(() => setLoadingTopCreators(false)),

        getTopPerformingPosts(dateFilterPayload)
          .then((res) => {
            if (res?.data) setTopPostsData(res.data);
          })
          .finally(() => setLoadingTopPosts(false)),

        getImpressionsAcrossPlatforms(dateFilterPayload)
          .then((res) => {
            if (res?.data) setImpressionsData(res.data);
          })
          .finally(() => setLoadingImpressions(false)),

        getContentCountStats(dateFilterPayload)
          .then((res) => {
            if (res?.data) setContentCountData(res.data);
          })
          .finally(() => setLoadingContentCount(false)),

        getEngagementBreakdownStats(dateFilterPayload)
          .then((res) => {
            if (res?.data) setEngagementBreakdownData(res.data);
          })
          .finally(() => setLoadingEngagement(false)),
      ]);
    } catch (error) {
      console.error('Error loading advanced stats:', error);
    } finally {
      setLoadingAdvancedStats(false);
    }
  };

  useEffect(() => {
    if (id && apiConfig.getStats) {
      loadAdvancedStats(dateRange);
    }
  }, [id, apiConfig.getStats]);

  const refetchStatsWithDateRange = (newDateRange) => {
    if (isTrackingInProgress) return;
    const body = { campaignId: id };
    if (newDateRange[0] && newDateRange[1]) {
      body.startDate = dayjs(newDateRange[0]).format('YYYY-MM-DD');
      body.endDate = dayjs(newDateRange[1]).format('YYYY-MM-DD');
    }
    refetchStats({ args: body });
    loadAdvancedStats(newDateRange); // Reload advanced stats with new date range
  };

  function onDateRangeChange(dateRange) {
    setDateRange(dateRange);
    setContentCurrentPage(1);
    refetchStatsWithDateRange(dateRange);
    refetchContentWithDateRange(dateRange);
  }

  const getContentFilterBody = (newFilter, dateRange) => {
    if (isTrackingInProgress) return;
    const body = {
      ...defaultGetPostsArgs,
      contentType: selectedContentType,
      platform: selectedPlatformType,
      listIds: selectedLists.map((item) => item._id),
      emailCampaignIds: selectedCampaigns.map((item) => item._id),
      labelIds: selectedLabels === -1 ? -1 : selectedLabels.map((item) => item._id),
    };
    if (body.platform === 'all') {
      body.platform = undefined;
    }
    if (body.contentType === 'all') {
      body.contentType = undefined;
    }
    if (dateRange) {
      if (dateRange[0] && dateRange[1]) {
        body.startDate = new Date(dateRange[0]).toISOString();
        body.endDate = new Date(dateRange[1]).toISOString();
      }
    }

    return { ...body, ...newFilter };
  };

  const setNewContentAndPlatformType = async (newContentType, newPlatform) => {
    setContentCurrentPage(1);
    if (isTrackingInProgress) return;
    setSelectedContentType(newContentType);
    setSelectedPlatformType((prev) => (newPlatform ? newPlatform : prev));
    const body = getContentFilterBody(
      {
        contentType: newContentType,
        platform: newPlatform ? newPlatform : selectedPlatformType,
      },
      dateRange
    );
    if (body.platform === 'all') {
      body.platform = undefined;
    }
    if (body.contentType === 'all') {
      body.contentType = undefined;
    }
    const res = await refetchPosts({
      args: pickBy(body, identity),
      returnOnly: true,
    });
    if (res?.status) {
      setContent({
        posts: [...res.data.posts],
        totalPosts: res?.data?.totalPostsCount,
      });
      setIsNextContent({
        isNext: res?.data?.isNext,
        nextCursor: res?.data?.nextCursor,
        page: res?.data?.page,
        totalPages: getToalPages(res?.data?.totalPostsCount),
      });
      recalculateGrid();
    }
  };

  async function refetchContent(args) {
    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const res = await refetchPosts({
        args: args,
        returnOnly: true,
        signal: abortControllerRef.current.signal,
      });

      if (res?.status) {
        setContent({
          posts: [...res.data.posts],
          totalPosts: res?.data?.totalPostsCount,
        });
        setIsNextContent({
          isNext: res?.data?.isNext,
          nextCursor: res?.data?.nextCursor,
          page: res?.data?.page,
          totalPages: getToalPages(res?.data?.totalPostsCount),
        });
        recalculateGrid();
      }
      return;
    } catch (error) {
      // Only log error if it's not an abort error
      if (error.name !== 'AbortError') {
        console.error('Error fetching content:', error);
      }
    } finally {
      // Clear the abortController reference if it's the current one
      if (abortControllerRef.current?.signal.aborted) {
        abortControllerRef.current = null;
      }
    }
  }

  const refetchContentWithDateRange = async (newDateRange) => {
    if (isTrackingInProgress) return;
    const newBody = { startDate: null, endDate: null };
    if (newDateRange[0] && newDateRange[1]) {
      newBody.startDate = dayjs(newDateRange[0]).format('YYYY-MM-DD');
      newBody.endDate = dayjs(newDateRange[1]).format('YYYY-MM-DD');
    }
    await refetchContent(getContentFilterBody(newBody, newDateRange));
  };

  const onSearch = async (newText) => {
    if (isTrackingInProgress) return;
    const newBody = { searchQuery: newText.length ? newText : null };
    refetchContent(getContentFilterBody(newBody, dateRange));
  };

  const onRemoveFilter = async (filterName) => {
    setContentCurrentPage(1);
    const newFilter = { ...getContentFilterBody({}, dateRange) };
    switch (filterName) {
      case SOCIAL_LISTENING_FILTERS.CONTENT_TYPE:
        setSelectedContentType('all');
        newFilter.contentType = undefined;
        break;
      case SOCIAL_LISTENING_FILTERS.PLATFORM:
        setSelectedPlatformType('all');
        newFilter.platform = undefined;
        break;
      case SOCIAL_LISTENING_FILTERS.CAMPAIGN:
        setSelectedCampaigns([]);
        newFilter.emailCampaignIds = [];
        break;
      case SOCIAL_LISTENING_FILTERS.LISTS:
        setSelectedLists([]);
        newFilter.listIds = [];
        break;
      case SOCIAL_LISTENING_FILTERS.LABELS:
        setSelectedLabels([]);
        newFilter.labelIds = [];
        break;
      case SOCIAL_LISTENING_FILTERS.SORT_BY:
        setSortField('all');
        setSortOrder('asc');
        break;
      default:
        return;
    }
    refetchContent(newFilter);
  };

  const onPageChange = async (pageNumber) => {
    setIsLoadingMore(true);
    setContentCurrentPage(pageNumber);
    const res = await refetchContent(getContentFilterBody({ page: pageNumber }, dateRange));

    if (res?.status) {
      setContent(() => {
        // Filter out any duplicate posts from new data
        const newPosts = res.data.posts;

        return {
          posts: newPosts,
          totalPosts: res?.data?.totalPostsCount,
        };
      });
      setIsNextContent({
        isNext: res?.data?.isNext,
        nextCursor: res?.data?.nextCursor,
        page: res?.data?.page,
        totalPages: getToalPages(res?.data?.totalPostsCount),
      });
    }
    setIsLoadingMore(false);
  };

  const onLoadMoreClick = async () => {
    setIsLoadingMore(true);
    setContentCurrentPage(isNextContent.page + 1);
    const newBody = { page: isNextContent.page + 1 };
    const res = await refetchPosts({
      args: { ...defaultGetPostsArgs, ...newBody },
      returnOnly: true,
    });
    if (res?.status) {
      setContent((existingContent) => {
        // Get existing post IDs for deduplication
        const existingIds = new Set(existingContent.posts.map((post) => post._id));
        // Filter out any duplicate posts from new data
        const newPosts = res.data.posts.filter((post) => !existingIds.has(post._id));

        return {
          posts: [...existingContent.posts, ...newPosts],
          totalPosts: res?.data?.totalPostsCount,
        };
      });
      setIsNextContent({
        isNext: res?.data?.isNext,
        nextCursor: res?.data?.nextCursor,
        page: res?.data?.page,
        totalPages: getToalPages(res?.data?.totalPostsCount),
      });
      recalculateGrid();
    }
    setIsLoadingMore(false);
  };

  async function setNewCampaigns(campaigns) {
    setSelectedCampaigns(campaigns);
    setContentCurrentPage(1);
    refetchContent(
      getContentFilterBody(
        {
          emailCampaignIds: campaigns.map((item) => item._id),
        },
        dateRange
      )
    );
  }

  function setNewLabels(newLabels) {
    setSelectedLabels(newLabels);
    setContentCurrentPage(1);
    const body =
      newLabels === -1
        ? {
            labelIds: -1,
          }
        : {
            labelIds: newLabels.map((item) => item._id),
          };
    refetchContent(getContentFilterBody(body, dateRange));
  }

  function setNewLists(lists) {
    setSelectedLists(lists);
    setContentCurrentPage(1);
    refetchContent(
      getContentFilterBody(
        {
          listIds: lists.map((item) => item._id),
        },
        dateRange
      )
    );
  }

  function updatePost(id, updatedPost) {
    setContent((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const postIndex = copy.posts.findIndex((post) => post._id === id);
      if (postIndex !== -1) {
        copy.posts[postIndex] = {
          ...copy.posts[postIndex],
          ...updatedPost,
        };
      }
      return copy;
    });
  }

  function clearAllContentFilters() {
    setSelectedContentType('all');
    setSelectedPlatformType('all');
    setSelectedCampaigns([]);
    setSelectedLists([]);
    setSelectedLabels([]);
    setSortField('all');
    setSortOrder('desc');
    setContentCurrentPage(1);
    refetchContent(
      getContentFilterBody({ ...defaultGetPostsArgs, contentType: undefined, platform: undefined }, dateRange)
    );
  }

  function refetchContentWithDefaultArgs() {
    refetchContent({ ...defaultGetPostsArgs, page: 1 });
  }

  function clearAllUsageRequestsFilters() {
    setUsageRequestsPage(1);
    setUsageRightsContentType('all');
    setNewUsageRequestsStatus('all');
    refetchUsageRequests({
      args: {
        campaignId: id,
        page: 1,
      },
    });
  }

  function getAppliedFilters() {
    const appliedFilters = [];
    if (selectedPlatformType !== 'all') {
      appliedFilters.push(SOCIAL_LISTENING_FILTERS.PLATFORM);
    }
    if (selectedContentType !== 'all') {
      appliedFilters.push(SOCIAL_LISTENING_FILTERS.CONTENT_TYPE);
    }
    if (selectedLists?.length) {
      appliedFilters.push(SOCIAL_LISTENING_FILTERS.LISTS);
    }
    if (selectedLabels?.length || selectedLabels === -1) {
      appliedFilters.push(SOCIAL_LISTENING_FILTERS.LABELS);
    }
    if (selectedCampaigns?.length) {
      appliedFilters.push(SOCIAL_LISTENING_FILTERS.CAMPAIGN);
    }
    return appliedFilters;
  }

  function getAppliedSort() {
    return sortField !== 'all' ? [sortField] : [];
  }

  // usage rights
  const [deleteTemplateModal, setDeleteTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreateNewModal, setIsCreateNewModal] = useState(true);
  const [isSystemTemplate, setIsSystemTemplate] = useState(false);
  const [createNewModal, setCreateNewModal] = useState(false);
  const [message, setMessage] = useState('');

  const [activeFilterKey, setActiveFilterKey] = useState(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <CampaignDetailContext.Provider
      value={{
        id,
        activeTab,
        setActiveTab,
        loadingStats,
        campaignStats,
        dateRange,
        setDateRange: onDateRangeChange,
        selectedDays,
        setSelectedDays,
        selectedView,
        setSelectedView,
        selectedContentType,
        setNewContentType: (value) => setNewContentAndPlatformType(value, selectedPlatformType),
        selectedPlatformType,
        setNewPlatformType: (value) => setNewContentAndPlatformType(selectedContentType, value),
        refetchUsageRequests,
        usageRequestsPage,
        usageRequestsPages,
        setUsageRequestsPage: (page) => {
          setUsageRequestsPage(page);
          refetchUsageRequestsWtihArgs({
            page,
          });
        },
        loadingContent,
        content,
        gridRef,
        isTrackingInProgress,
        showChartFtux,
        loadingCampaignDetails,
        campaignDetails,
        refetchCampaignDetails,
        onSearch,
        onRemoveFilter,
        isNextContent,
        onLoadMoreClick,
        onPageChange,
        contentCurrentPage,
        refetchContentWithDefaultArgs,
        isLoadingMore,
        selectedLists,
        setSelectedLists: setNewLists,
        selectedCampaigns,
        setSelectedCampaigns: setNewCampaigns,
        updatePost,
        usageRequests,
        usageRightsContentType,
        usageRightsStatus,
        setNewUsageRequestsContentType,
        setNewUsageRequestsStatus,
        clearAllContentFilters,
        clearAllUsageRequestsFilters,
        labelModalOpen,
        setLabelModalOpen,
        selectedLabels,
        setNewLabels,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        getAppliedFilters,
        getAppliedSort,
        // usage rights
        deleteTemplateModal,
        setDeleteTemplateModal,
        currentModal,
        setCurrentModal,
        selectedTemplate,
        setSelectedTemplate,
        isCreateNewModal,
        setIsCreateNewModal,
        isSystemTemplate,
        setIsSystemTemplate,
        createNewModal,
        setCreateNewModal,
        message,
        setMessage,

        activeFilterKey,
        setActiveFilterKey,
        // Advanced stats
        loadingAdvancedStats,
        keyPerformanceData,
        totalViewsData,
        topCreatorsData,
        topPostsData,
        impressionsData,
        contentCountData,
        engagementBreakdownData,
        loadAdvancedStats,
        // Individual loading states
        loadingKPI,
        loadingTotalViews,
        loadingTopCreators,
        loadingTopPosts,
        loadingImpressions,
        loadingContentCount,
        loadingEngagement,
      }}>
      {children}
    </CampaignDetailContext.Provider>
  );
};

export const useCampaignDetail = () => {
  return useContext(CampaignDetailContext);
};

export default CampaignDetailProvider;
